// 动画效果函数
const Tween = {
  linear(t, b, c, d) {
    return (c * t) / d + b;
  },
  easeIn(t, b, c, d) {
    return t === 0 ? b : c * Math.pow(2, 10 * (t / d - 1)) + b;
  },
  easeOut(t, b, c, d) {
    return t === d ? b + c : c * (-Math.pow(2, (-10 * t) / d) + 1) + b;
  },
  easeInOut(t, b, c, d) {
    if (t === 0) return b;
    if (t === d) return b + c;
    const tm = t / (d / 2);
    if (tm < 1) return (c / 2) * Math.pow(2, 10 * (tm - 1)) + b;
    return (c / 2) * (-Math.pow(2, -10 * (t - 1)) + 2) + b;
  },
};

// 获取当前滚动条位置
export const currentYPosition = () =>
  document.documentElement.scrollTop || window.pageYOffset || document.body.scrollTop;

// 获取目标元素的位置
const _elmYPosition = (eId) => {
  const ele = document.getElementById(eId);
  return ele.offsetTop + 1;
};

// 滚动到指定元素位置
export const scrollTo = (pos, during = 60, ease = 'linear') => {
  //  requestAnimationFrame 兼容性写法
  let lastTime = 0;
  const vendors = ['webkit', 'moz'];
  for (let x = 0; x < vendors.length && !window.requestAnimationFrame; ++x) {
    window.requestAnimationFrame = window[`${vendors[x]}RequestAnimationFrame`];
    window.cancelAnimationFrame =
      window[`${vendors[x]}CancelAnimationFrame`] || window[`${vendors[x]}CancelRequestAnimationFrame`];
  }

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame = function (callback) {
      const currTime = new Date().getTime();
      const timeToCall = Math.max(0, 16 - (currTime - lastTime));
      const id = window.setTimeout(() => {
        callback(currTime + timeToCall);
      }, timeToCall);
      lastTime = currTime + timeToCall;
      return id;
    };
  }

  if (!window.cancelAnimationFrame) {
    window.cancelAnimationFrame = function (id) {
      clearTimeout(id);
    };
  }

  const scrollY = currentYPosition();
  let targetY;
  if (typeof pos === 'string') {
    targetY = _elmYPosition(pos);
  } else if (typeof pos === 'number') {
    targetY = pos;
  } else {
    throw new Error('Pos must be id or y position');
  }

  let start = 0;
  let stop;
  const _run = function () {
    start++;
    const top = Tween[ease](start, scrollY, targetY - scrollY, during);
    window.scrollTo(0, top);
    if (start < during) {
      stop = requestAnimationFrame(_run);
    }
  };
  _run();

  return stop;
};

export const stopScroll = (stop) => {
  cancelAnimationFrame(stop);
};

export function addSwipeListener(
  container: HTMLElement,
  onTouching: (direction: number, distance: number) => void,
  onTouchEnd: (direction: number, distance: number) => void,
) {
  if (!container || typeof document === 'undefined') {
    return; // 在服务器端或者找不到容器的情况下返回
  }
  let startX: number | undefined;
  let startY: number | undefined;
  let direction: number | undefined;
  let distance = 0;
  let isMouseDown = false;

  function preventDefaultHandler(e: MouseEvent | TouchEvent) {
    e.preventDefault();
  }

  const bindStopVerticalMove = (type: 'mousemove' | 'touchmove') => {
    document.addEventListener(type, preventDefaultHandler, { passive: false });
  };

  const unbindStopVerticalMove = (type: 'mousemove' | 'touchmove') => {
    document.removeEventListener(type, preventDefaultHandler);
  };

  const handleMouseDown = (e: MouseEvent) => {
    bindStopVerticalMove('mousemove');
    startX = e.clientX;
    startY = e.clientY;
    isMouseDown = true;
  };

  const handleTouchStart = (e: TouchEvent) => {
    bindStopVerticalMove('touchmove');
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (startX !== undefined && isMouseDown) {
      const currentX = e.clientX;
      const currentY = e.clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        bindStopVerticalMove('mousemove');
        distance = currentX - startX;
        direction = distance > 0 ? 1 : -1;
        onTouching(direction, Math.abs(distance));
      } else {
        unbindStopVerticalMove('mousemove');
      }
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (startX !== undefined) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        bindStopVerticalMove('touchmove');
        distance = currentX - startX;
        direction = distance > 0 ? 1 : -1;
        onTouching(direction, Math.abs(distance));
      } else {
        unbindStopVerticalMove('touchmove');
      }
    }
  };

  const handleMouseUp = () => {
    unbindStopVerticalMove('mousemove');
    isMouseDown = false;
    if (direction) {
      onTouchEnd(direction, Math.abs(distance));
      startX = undefined;
      direction = undefined;
      distance = 0;
    }
  };

  const handleTouchEnd = () => {
    unbindStopVerticalMove('touchmove');
    if (direction) {
      onTouchEnd(direction, Math.abs(distance));
      startX = undefined;
      direction = undefined;
      distance = 0;
    }
  };

  container.addEventListener('mousedown', handleMouseDown);
  container.addEventListener('touchstart', handleTouchStart);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('touchmove', handleTouchMove);
  document.addEventListener('mouseup', handleMouseUp);
  document.addEventListener('touchend', handleTouchEnd);

  return () => {
    container.removeEventListener('mousedown', handleMouseDown);
    container.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.removeEventListener('touchend', handleTouchEnd);
  };
}
